/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react";
import Button from "../Shared/Button";
import { useLocation } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { queryClient } from "../../main";
import { getUserCountry, hasDiscount } from "../../utils/utils";
import Input from "../Shared/Input";
import { GetCouponById } from "../../api/Coupon/GetCouponById";
import Spinner from "../Shared/Spinner";
import { CheckoutCart } from "../../api/Cart/CheckoutCart";
import { CartDTO } from "../../api/Cart/GetCart";
import PaymentSuccessModal from "../Shared/PaymentSuccessModal";
import PaymentFailedModal from "../Shared/PaymentFailedModal";
import { DeleteCartItem } from "../../api/Cart/DeleteCartItems";
import { DeleteCartPackage } from "../../api/Cart/DeletePackageFromCart";
import TrashIcon from "../Icons/TrashIcon";
import { DeleteCartBook } from "../../api/Cart/DeleteBookFromCart";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import * as Yup from "yup";
import { ButtonTypes } from "../../utils/consts";
import { ApplyCoupon } from "../../api/Coupon/ApplyCoupon";
import EnrolmentTermsAgreement from "../Shared/EnrolmentTermsAgreement";
import { setPendingPurchase, firePurchaseFromSuccess } from "../../utils/tracking";
import { getApiErrorMessage } from "../../utils/apiError";
const validationSchema = Yup.object().shape({
  billing_data: Yup.object().shape({
    street: Yup.string().required("Street Name is required"),
    city: Yup.string().required("City is required"),
    country: Yup.string().required("Country is required"),
  }),
});

const CartPaymentDetails = ({ cart }: { cart: CartDTO }) => {
  const location = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [country, setCountry] = useState<string | null>(null);
  const [couponId, setCouponId] = useState<string>("");
  const [appliedCouponCode, setAppliedCouponCode] = useState<string>("");
  const [couponDiscount, setCouponDiscount] = useState<number>(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPaymentSuccess, setIsPaymentSuccess] = useState(false);
  const [isPaymentFailed, setIsPaymentFailed] = useState(false);
  const [isFormVisible, setIsFormVisible] = useState<boolean>(false);
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    const userCountry = getUserCountry();
    setCountry(userCountry);
  }, []);
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get("success") === "true") {
      setIsPaymentSuccess(true);
      setIsModalOpen(true);
      // Record the Meta Pixel Purchase event so Sales campaigns can optimise + report ROAS.
      firePurchaseFromSuccess(searchParams);
    } else if (searchParams.get("failed") === "true") {
      setIsPaymentFailed(true);
      setIsModalOpen(true);
    }
  }, [location.search]);

  const closeModal = () => {
    setIsModalOpen(false);
    setIsPaymentSuccess(false);
    setIsPaymentFailed(false);
  };

  const discountPrice =
    country === "EG"
      ? cart?.total_discountPrice_egp
      : cart?.total_discountPrice_usd;

  const couponDiscountedPrice = discountPrice
    ? discountPrice - (discountPrice * couponDiscount) / 100
    : 0;

  const enrollMutation = useMutation({
    //@ts-expect-error
    mutationFn: (payload) => CheckoutCart(payload),
    onSuccess: (data) => {
      if (data && data.checkoutLink) {
        window.open(data.checkoutLink, "_blank");
      }
    },
    onError: () => {
      setIsSubmitting(false);
    },
  });

  const handleCheckout = () => {
    if (cart.books.length > 0 && !isFormVisible) {
      setIsFormVisible(true);
    } else {
      if (isFormVisible) {
        formik.handleSubmit();
      } else {
        const basePayload = {
          amount_cents: couponDiscountedPrice.toFixed(2),
          currency: country === "EG" ? "EGP" : "USD",
          ...(appliedCouponCode && { couponCode: appliedCouponCode }),
        };
        // Remember the amount so we can report it as Purchase value on the success redirect.
        setPendingPurchase(couponDiscountedPrice, country === "EG" ? "EGP" : "USD");
        setIsSubmitting(true);
        //@ts-expect-error
        enrollMutation.mutate(basePayload);
      }
    }
  };

  const { mutate: DeleteCart } = useMutation({
    mutationFn: DeleteCartItem,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cart"] });
      toast.success("Course Removed from cart successfully");
    },
  });
  const { mutate: DeletePackageCart } = useMutation({
    mutationFn: DeleteCartPackage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cart"] });
      toast.success("Package Removed from cart successfully");
    },
  });
  const { mutate: DeleteBookCart } = useMutation({
    mutationFn: DeleteCartBook,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cart"] });
      toast.success("Book Removed from cart successfully");
    },
  });

  const applyCouponMutation = useMutation({
    mutationFn: async (code: string) => {
      await ApplyCoupon(code);
      const couponResponse = await GetCouponById(code);

      if (couponResponse.error) {
        throw couponResponse.error;
      }

      return couponResponse;
    },
    onSuccess: (data, code) => {
      if (
        data &&
        data.data &&
        data.data.CouponData &&
        data.data.CouponData.length > 0
      ) {
        queryClient.setQueryData(["coupon", code], data);
        setCouponDiscount(data.data.CouponData[0].discount || 0);
        setAppliedCouponCode(data.data.CouponData[0].code || code);
        setErrorMessage(null);
      } else {
        setCouponDiscount(0);
        setAppliedCouponCode("");
        setErrorMessage("Coupon not found");
      }
    },
    onError: (error: unknown) => {
      setCouponDiscount(0);
      setAppliedCouponCode("");
      setErrorMessage(
        getApiErrorMessage(error, "We could not apply this coupon")
      );
    },
  });

  const handleApplyCoupon = () => {
    const code = couponId.trim();

    setErrorMessage(null);
    setCouponDiscount(0);
    setAppliedCouponCode("");

    if (!code) {
      setErrorMessage("Enter a coupon code");
      return;
    }

    applyCouponMutation.mutate(code);
  };
  const formik = useFormik({
    initialValues: {
      amount_cents: couponDiscountedPrice.toFixed(2),
      currency: country === "EG" ? "EGP" : "USD",
      billing_data: {
        street: "",
        building: "",
        city: "",
        country: "",
        landing: "",
        floor: "",
        state: "",
        apartment: "",
      },
    },
    validationSchema,
    onSubmit: (values) => {
      const payload = {
        ...values,
        currency: country === "EG" ? "EGP" : "USD",
        amount_cents: couponDiscountedPrice.toFixed(2),
        ...(appliedCouponCode && { couponCode: appliedCouponCode }),
      };
      // Remember the amount so we can report it as Purchase value on the success redirect.
      setPendingPurchase(couponDiscountedPrice, country === "EG" ? "EGP" : "USD");
      //@ts-expect-error
      enrollMutation.mutate(payload); // Send complete payload with billing data
      setIsSubmitting(true);
    },
  });
  return (
    <div
      className={`w-full  flex gap-10 lg:flex-row-reverse  pb-20 ${isFormVisible ? "flex-col-reverse" : "flex-col"
        }`}
    >
      <PaymentFailedModal
        isOpen={isPaymentFailed && isModalOpen}
        closeModal={closeModal}
      />
      <PaymentSuccessModal
        isOpen={isPaymentSuccess && isModalOpen}
        closeModal={closeModal}
      />

      <div className="w-full xl:w-full lg:w-1/2">
        {isFormVisible && cart.books.length > 0 ? (
          <form onSubmit={formik.handleSubmit} className="space-y-3 sm:space-y-5">
            <h1 className="sm:text-0.5xl text-lg -tracking-[0.02em] text-burntBlack font-semibold">
              Please enter your billing data for shipping:
            </h1>
            <div className="flex gap-2 sm:gap-4">
              <Input
                value={formik.values.billing_data.street}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.billing_data?.street &&
                    formik.errors.billing_data?.street
                    ? formik.errors.billing_data.street
                    : ""
                }
                name="billing_data.street"
                inputStyles="sm:!h-14 !h-12"
                placeholder="Street Name"
                className="w-full"
              />
            </div>
            <div className="flex gap-2 sm:gap-4">
              <Input
                value={formik.values.billing_data.country}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.billing_data?.country &&
                    formik.errors.billing_data?.country
                    ? formik.errors.billing_data?.country
                    : ""
                }
                name="billing_data.country"
                inputStyles="sm:!h-14 !h-12"
                placeholder="Country"
                className="w-full"
              />
              <Input
                value={formik.values.billing_data.city}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.billing_data?.city &&
                    formik.errors.billing_data?.city
                    ? formik.errors.billing_data?.city
                    : ""
                }
                name="billing_data.city"
                inputStyles="sm:!h-14 !h-12"
                placeholder="City"
                className="w-full"
              />
              <Input
                value={formik.values.billing_data.state}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.billing_data?.state &&
                    formik.errors.billing_data?.state
                    ? formik.errors.billing_data?.state
                    : ""
                }
                name="billing_data.state"
                inputStyles="sm:!h-14 !h-12"
                placeholder="State"
                className="w-full"
              />
            </div>
            <Input
              value={formik.values.billing_data.landing}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.billing_data?.landing &&
                  formik.errors.billing_data?.landing
                  ? formik.errors.billing_data?.landing
                  : ""
              }
              name="billing_data.landing"
              inputStyles="sm:!h-14 !h-12"
              placeholder="Special Landing"
              className="w-full"
            />
            <div className="flex gap-2 sm:gap-4 ">
              <Input
                value={formik.values.billing_data.building}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.billing_data?.building &&
                    formik.errors.billing_data?.building
                    ? formik.errors.billing_data?.building
                    : ""
                }
                name="billing_data.building"
                inputStyles="sm:!h-14 !h-12"
                placeholder="Building"
                className="w-full"
              />
              <Input
                value={formik.values.billing_data.floor}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.billing_data?.floor &&
                    formik.errors.billing_data?.floor
                    ? formik.errors.billing_data?.floor
                    : ""
                }
                name="billing_data.floor"
                inputStyles="sm:!h-14 !h-12"
                placeholder="Floor"
                className="w-full"
              />
              <Input
                value={formik.values.billing_data.apartment}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.billing_data?.apartment &&
                    formik.errors.billing_data?.apartment
                    ? formik.errors.billing_data?.apartment
                    : ""
                }
                name="billing_data.apartment"
                inputStyles="sm:!h-14 !h-12"
                placeholder="Apartment"
                className="w-full"
              />
            </div>
            <Button
              variant="primary"
              type={ButtonTypes.SUBMIT}
              className="w-full sm:!h-16 !h-11 sm:!text-xl !text-base mb-1 sm:mb-3"
              onClick={handleCheckout}
              disabled={isSubmitting || !agreed}
            >
              {isSubmitting ? "Processing..." : "Checkout Now"}
            </Button>

          </form>

        ) : (
          <>
            {cart?.courses.length > 0 && (
              <div className="border border-black/20 sm:pl-3 sm:pr-7 px-3 py-2.5 shadow rounded-[10px] mb-5 xl:w-[600px] w-full space-y-4">
                {cart?.courses.map((item) => (
                  <div className="relative flex flex-col items-center gap-5 sm:flex-row">
                    <button
                      onClick={() => DeleteCart(item.id)}
                      className="absolute bottom-0 right-0 text-red-500 sm:top-2 sm:bottom-auto sm:-right-4"
                    >
                      <TrashIcon />
                    </button>

                    <img
                      className="sm:w-[150px] sm:h-[130px] h-[250px] w-full rounded-2xl object-fit"
                      src={item?.img_url}
                      alt="Course"
                    />
                    <div className="w-full">
                      <h1 className="mb-4 text-lg font-bold leading-none text-burntBlack">
                        {item?.name}
                      </h1>
                      <h1 className="font-bold text-burntBlack text-lg -tracking-[0.02em] leading-none">
                        {country === "EG" ? (
                          <>
                            {" "}
                            {hasDiscount(item?.actualPrice_egp, item?.discountPrice_egp)
                              ? item?.discountPrice_egp
                              : item?.actualPrice_egp}{" "}
                            EGP{" "}
                            {hasDiscount(
                              item?.actualPrice_egp,
                              item?.discountPrice_egp
                            ) && (
                              <span className="text-gray-400 line-through">
                                {item?.actualPrice_egp} EGP
                              </span>
                            )}{" "}
                          </>
                        ) : (
                          <>
                            {" "}
                            {hasDiscount(item?.actualPrice_usd, item?.discountPrice_usd)
                              ? item?.discountPrice_usd
                              : item?.actualPrice_usd}{" "}
                            USD{" "}
                            {hasDiscount(
                              item?.actualPrice_usd,
                              item?.discountPrice_usd
                            ) && (
                              <span className="text-gray-400 line-through">
                                {item?.actualPrice_usd} USD
                              </span>
                            )}{" "}
                          </>
                        )}
                      </h1>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {cart?.books.length > 0 && (
              <div className="border border-black/20 sm:pl-3 sm:pr-7 px-3 py-2.5 shadow rounded-[10px] mb-5 xl:w-[600px] w-full space-y-4">
                {cart?.books.map((item) => (
                  <div className="relative flex flex-col items-center gap-5 sm:flex-row">
                    <button
                      onClick={() => DeleteBookCart(item.id || "")}
                      className="absolute bottom-0 right-0 text-red-500 sm:top-2 sm:bottom-auto sm:-right-4"
                    >
                      <TrashIcon />
                    </button>

                    <img
                      className="sm:w-[150px] sm:h-[130px] h-[250px] w-full rounded-2xl object-fit"
                      src={item?.img_url}
                      alt="Book"
                    />
                    <div className="w-full">
                      <h1 className="mb-4 text-lg font-bold leading-none text-burntBlack">
                        {item?.name}
                      </h1>
                      <h1 className="font-bold text-burntBlack text-lg -tracking-[0.02em] leading-none">
                        {country === "EG" ? (
                          <>
                            {" "}
                            {hasDiscount(item?.public_EGP_price, item?.discountPrice_egp)
                              ? item?.discountPrice_egp
                              : item?.public_EGP_price}{" "}
                            EGP{" "}
                            {hasDiscount(
                              item?.public_EGP_price,
                              item?.discountPrice_egp
                            ) && (
                              <span className="text-gray-400 line-through">
                                {item?.public_EGP_price} EGP
                              </span>
                            )}{" "}
                          </>
                        ) : (
                          <>
                            {" "}
                            {hasDiscount(item?.public_USD_price, item?.discountPrice_usd)
                              ? item?.discountPrice_usd
                              : item?.public_USD_price}{" "}
                            USD{" "}
                            {hasDiscount(
                              item?.public_USD_price,
                              item?.discountPrice_usd
                            ) && (
                              <span className="text-gray-400 line-through">
                                {item?.public_USD_price} USD
                              </span>
                            )}{" "}
                          </>
                        )}
                      </h1>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {cart?.packages.length > 0 && (
              <div className="border border-black/20 sm:pl-3 sm:pr-7 px-3 py-2.5 shadow rounded-[10px] mb-5 xl:w-[600px] w-full space-y-4">
                {cart?.packages.map((item) => (
                  <div className="relative flex flex-col items-center gap-5 sm:flex-row">
                    <button
                      onClick={() => DeletePackageCart(item.id)}
                      className="absolute bottom-0 right-0 text-red-500 sm:top-2 sm:bottom-auto sm:-right-4"
                    >
                      <TrashIcon />
                    </button>

                    <div className="flex gap-1 min-w-[150px] sm:w-[150px] sm:h-[130px] h-[250px] rounded-2xl overflow-hidden">
                      {item.courses.map((course, index) => (
                        <img
                          src={course.img_url}
                          className={`w-1/2 object-fit ${index === 0 ? "object-left" : "object-right"
                            }`}
                        />
                      ))}
                    </div>
                    <div className="w-full">
                      <h1 className="mb-4 text-lg font-bold leading-none text-burntBlack">
                        {item?.name}
                      </h1>
                      <h1 className="font-bold text-burntBlack text-lg -tracking-[0.02em] leading-none">
                        {country === "EG" ? (
                          <>
                            {" "}
                            {hasDiscount(item?.actualPrice_egp, item?.discountPrice_egp)
                              ? item?.discountPrice_egp
                              : item?.actualPrice_egp}{" "}
                            EGP{" "}
                            {hasDiscount(
                              item?.actualPrice_egp,
                              item?.discountPrice_egp
                            ) && (
                              <span className="text-gray-400 line-through">
                                {item?.actualPrice_egp} EGP
                              </span>
                            )}{" "}
                          </>
                        ) : (
                          <>
                            {" "}
                            {hasDiscount(item?.actualPrice_usd, item?.discountPrice_usd)
                              ? item?.discountPrice_usd
                              : item?.actualPrice_usd}{" "}
                            USD{" "}
                            {hasDiscount(
                              item?.actualPrice_usd,
                              item?.discountPrice_usd
                            ) && (
                              <span className="text-gray-400 line-through">
                                {item?.actualPrice_usd} USD
                              </span>
                            )}{" "}
                          </>
                        )}
                      </h1>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>

        )}
      </div>
      <div className="xl:w-full xl:min-w-[600px] lg:w-1/2 w-full">
        <div className="border border-black/20 md:px-10 px-4 py-9 shadow rounded-[10px] mb-4">
          <div className="sm:space-y-[18px] space-y-4 sm:pb-8 pb-5 sm:mb-8 mb-5 border-b border-black/20">
            <div className="flex items-center justify-between gap-5">
              <span className="sm:text-0.5xl text-lg -tracking-[0.02em] text-lightGray font-medium">
                Price
              </span>
              <span className="sm:text-xl text-base -tracking-[0.02em] text-burntBlack font-semibold">
                {country === "EG" ? (
                  <> {cart?.total_actualPrice_egp} EGP</>
                ) : (
                  <>{cart?.total_actualPrice_usd} USD</>
                )}
              </span>
            </div>
            <div className="flex items-center justify-between gap-5">
              <span className="sm:text-0.5xl text-lg -tracking-[0.02em] text-lightGray font-medium">
                Price After Discount
              </span>
              <span className="sm:text-xl text-base -tracking-[0.02em] text-burntBlack font-semibold">
                {discountPrice} {country === "EG" ? "EGP" : "USD"}
              </span>
            </div>
            <div className="flex items-center justify-between gap-5">
              <span className="sm:text-0.5xl text-lg -tracking-[0.02em] text-lightGray font-medium">
                Extra Coupon Discount
              </span>
              <span className="sm:text-xl text-base -tracking-[0.02em] text-burntBlack font-semibold">
                {couponDiscount}%
              </span>
            </div>
            <div className="flex items-center justify-between gap-5">
              <span className="sm:text-0.5xl text-lg -tracking-[0.02em] text-lightGray font-medium">
                Tax
              </span>
              <span className="sm:text-xl text-base -tracking-[0.02em] text-burntBlack font-semibold">
                0 {country === "EG" ? "EGP" : "USD"}
              </span>
            </div>
          </div>
          <div className="flex items-center justify-between gap-5">
            <span className="sm:text-0.5xl text-lg -tracking-[0.02em] text-burntBlack font-semibold">
              Total Price After Discount
            </span>
            <span className="sm:text-xl -tracking-[0.02em] text-burntBlack font-semibold">
              {couponDiscountedPrice} {country === "EG" ? "EGP" : "USD"}
            </span>
          </div>
        </div>
        <div className="relative mb-4">
          <Input
            name="coupon"
            inputStyles="sm:!h-12 !h-10"
            value={couponId}
            onChange={(e) => setCouponId(e.target.value)}
          />
          <Button
            variant="primary"
            className="absolute top-0 right-0 sm:!h-12 !h-10 sm:!text-sm text-xs px-4"
            onClick={handleApplyCoupon}
            disabled={applyCouponMutation.isPending}
          >
            Apply Coupon{" "}
            {applyCouponMutation.isPending && (
              <Spinner className="!ml-0 size-6" />
            )}
          </Button>
          {errorMessage && (
            <div className="my-2 text-sm font-bold text-red-500">
              {errorMessage}
            </div>
          )}
        </div>
        <EnrolmentTermsAgreement
          checked={agreed}
          onChange={setAgreed}
          className="mb-4"
        />
        {!isFormVisible &&
          <Button
            variant="primary"
            className="w-full sm:!h-16 !h-11 sm:!text-xl !text-base mb-1 sm:mb-3"
            onClick={handleCheckout}
            disabled={isSubmitting || !agreed}
          >
            {isSubmitting ? "Processing..." : "Checkout Now"}
          </Button>
        }
      </div>
    </div>
  );
};

export default CartPaymentDetails;
